import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { playAudio } from '../../../../utils/Utils';
import GLOBAL from '../../../../global';

/**
 * Custom hook for managing chatbot audio playback
 * Handles question/answer audio sequencing with 1-second delay
 * and stopping audio on user interaction.
 */
const useChatbotAudio = () => {
    const chatBotNewState = useSelector(state => state.chatBotNew);
    const { flatQuestions, currentQuestionIndex } = chatBotNewState;

    const currentQuestion = flatQuestions[currentQuestionIndex];
    const prevQuestionRef = useRef(null);
    const isPlayingRef = useRef(false);
    const abortControllerRef = useRef(null);
    const sequenceTimeoutRef = useRef(null);

    /**
     * Play question voice followed by answers voice with 1-second delay
     */
    const playQuestionAndAnswers = useCallback(async (question, allQuestions) => {
        if (!question || isPlayingRef.current) return;
        if (!GLOBAL.AUDIO_PLAY_ENABLED) return;

        // Cancel any ongoing sequence
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        const { signal } = abortControllerRef.current;

        isPlayingRef.current = true;

        try {
            // Play question voice first
            if (question.voiceUrl && !signal.aborted) {
                await playAudio(question.voiceUrl, { priorityLevel: 3, ignoreInterval: true });
            }

            // Wait 500ms before playing answers (reduced from 1000ms since queue handles sequencing)
            if (!signal.aborted) {
                await new Promise(resolve => {
                    sequenceTimeoutRef.current = setTimeout(resolve, 500);
                });
            }

            // Play each response voice sequentially using queue mode
            // Queue mode bypasses the 2-second priority 3 interval blocking
            if (question.responses && !signal.aborted) {
                for (const response of question.responses) {
                    if (signal.aborted) break;
                    if (response.voiceUrl) {
                        await playAudio(response.voiceUrl, { queue: true, priorityLevel: 3 });
                    }
                }
            }
        } catch (error) {
            console.log('Audio sequence interrupted or error:', error);
        } finally {
            isPlayingRef.current = false;
        }
    }, []);

    /**
     * Stop all currently playing audio immediately
     */
    const stopAudio = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        if (sequenceTimeoutRef.current) {
            clearTimeout(sequenceTimeoutRef.current);
        }
        isPlayingRef.current = false;

        // Remove any currently playing audio
        playAudio('remove').then(() => {
            console.log('Audio stopped due to user interaction');
        }).catch(() => {
            // Ignore errors when stopping
        });
    }, []);

    /**
     * Play audio when question changes
     */
    useEffect(() => {
        // Skip if no question or same question
        if (!currentQuestion) return;
        if (prevQuestionRef.current?.questionId === currentQuestion.questionId) {
            return;
        }

        // Check if question is already answered
        const isAnswered = !!(
            currentQuestion.answerId || 
            currentQuestion.answerValue || 
            (currentQuestion.answerList && currentQuestion.answerList.length > 0)
        );

        if (isAnswered) {
             stopAudio();
             prevQuestionRef.current = currentQuestion;
             return;
        }

        prevQuestionRef.current = currentQuestion;
        playQuestionAndAnswers(currentQuestion, flatQuestions);

        return () => {
            // Cleanup on unmount or question change
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
            if (sequenceTimeoutRef.current) {
                clearTimeout(sequenceTimeoutRef.current);
            }
        };
    }, [currentQuestion, flatQuestions, playQuestionAndAnswers]);

    /**
     * Cleanup on unmount
     */
    useEffect(() => {
        return () => {
            stopAudio();
        };
    }, [stopAudio]);

    return {
        stopAudio,
        isPlaying: isPlayingRef.current
    };
};

export default useChatbotAudio;
